import { FormOutlined } from '@ant-design/icons';
import { Pagination, Select } from 'antd';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isPending } from '../../constants/loading_state';
import { mapFishingReportIdsAndPositions } from '../../hooks/use_fishing_report_list_viewed';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { OmniaQueryParams } from '../../lib/query_string';
import { scrollToElementTop } from '../../lib/scroll';
import { WebAnalytics } from '../../lib/web_analytics';
import {
  getFacets,
  getFishingReports,
  getLoadingState,
  getTotalCount
} from '../../redux/fishing_reports';
import { FishingReportModalActions } from '../../redux/fishing_report_modal';
import { getSpecies, getStatesAlphabetical } from '../../redux/reference_data';
import { FishingReportCard } from '../fishing_report_card/fishing_report_card';
import Loading from '../loading/loading';
import { OmniaButton } from '../omnia_button/omnia_button';
import styles from './fishing_reports_list.less';

export const PER_PAGE_DEFAULT = 18;

export interface FishingReportsListQueryParams {
  [OmniaQueryParams.pg]: string;
  [OmniaQueryParams.per_page]: string;
  [OmniaQueryParams.species]: string;
  [OmniaQueryParams.season_group]: string;
  [OmniaQueryParams.waterbody]: string;
  [OmniaQueryParams.state]: string;
}

interface FishingReportsListProps {
  page: number;
  perPage: number;
}
export const FishingReportsList = (props: FishingReportsListProps) => {
  const { page, perPage } = props;
  const fishingReports = useSelector(getFishingReports);
  const loadingState = useSelector(getLoadingState);
  const dispatch = useDispatch();
  const allSpecies = useSelector(getSpecies);
  const allStates = useSelector(getStatesAlphabetical);
  const totalCount = useSelector(getTotalCount);
  const facets = useSelector(getFacets);
  const divRef = useRef<HTMLDivElement>();
  const { pushQueryString } = useQueryString();
  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<FishingReportsListQueryParams>();

  const selectSpecies = facets.species.length ? facets.species : allSpecies;
  const selectWaterbodies = facets.waterbodies;
  const selectSeasonGroups = facets.season_groups;

  const selectedSpeciesName = currentQuery.species;
  const selectedStateAbbr = currentQuery.state;
  const selectedWaterbodyId = currentQuery.waterbody;
  const selectedSeasonGroupName = currentQuery.season_group;

  const hasFilters =
    selectedSpeciesName || selectedStateAbbr || selectedWaterbodyId || selectedSeasonGroupName;

  useEffect(() => {
    if (fishingReports?.length > 0) {
      WebAnalytics.fishingReportListViewed(
        'embedded',
        mapFishingReportIdsAndPositions(fishingReports)
      );
    }
  }, [fishingReports.map((fr) => fr.id).join(',')]);

  const onPaginationChange = (newPage: number) => {
    scrollToElementTop(divRef.current);
    pushQueryString<FishingReportsListQueryParams>({
      pg: newPage.toString(),
    });
  };

  const { isSmallDesktop, isMobile } = useResponsive();

  let heroImageWidth: number;

  if (isSmallDesktop) {
    heroImageWidth = 435;
  } else if (!isSmallDesktop && !isMobile) {
    heroImageWidth = 478;
  }

  return (
    <section ref={divRef}>
      <div className={styles.buttonWrapper}>
        <OmniaButton
          kind="tertiary"
          size="lg"
          fontSize={14}
          onPress={() => {
            dispatch(FishingReportModalActions.FISHING_REPORT_MODAL_OPEN());
          }}
        >
          <FormOutlined /> Submit a Fishing Report
        </OmniaButton>
      </div>

      <section className={styles.filters}>
        <span className={styles.filterTitle}>{isMobile && `Choose Options to `}Filter By:</span>

        <Select
          className={styles.select}
          value={selectedStateAbbr}
          onChange={(value) => {
            pushQueryString<FishingReportsListQueryParams>({
              state: value,
              waterbody: null,
              species: null,
              season_group: null,
              pg: '1',
            });
          }}
          placeholder="State"
          allowClear
          options={allStates.map((state) => ({ value: state.abbr, label: state.name }))}
        />

        <Select
          className={styles.select}
          value={selectedWaterbodyId}
          onChange={(value) => {
            pushQueryString<FishingReportsListQueryParams>({
              waterbody: value,
              pg: '1',
            });
          }}
          placeholder="Lake"
          allowClear
          disabled={!selectedStateAbbr}
          options={selectWaterbodies.map((w) => ({ value: w.waterbody_id, label: w.primary_name }))}
        />

        <Select
          className={styles.select}
          value={selectedSpeciesName}
          onChange={(value) => {
            pushQueryString<FishingReportsListQueryParams>({
              species: value,
              pg: '1',
            });
          }}
          placeholder="Species"
          allowClear
          options={selectSpecies.map((specie) => ({
            value: specie.name,
            label: specie.display_name,
          }))}
        />

        <Select
          className={styles.select}
          value={selectedSeasonGroupName}
          onChange={(value) => {
            pushQueryString<FishingReportsListQueryParams>({
              season_group: value,
              pg: '1',
            });
          }}
          placeholder="Season"
          allowClear
          options={selectSeasonGroups.map((sg) => ({
            value: sg.name,
            label: sg.display_name,
          }))}
        />

        {hasFilters && (
          <div style={{ width: '100%', textAlign: 'center' }}>
            <OmniaButton
              kind="text"
              size="lg"
              fontSize={14}
              onPress={() => {
                pushQueryString<FishingReportsListQueryParams>({
                  waterbody: null,
                  species: null,
                  state: null,
                  season_group: null,
                  pg: '1',
                });
              }}
            >
              Clear Filters
            </OmniaButton>
          </div>
        )}
      </section>

      {isPending(loadingState) ? (
        <Loading text="Loading Fishing Reports" />
      ) : (
        <div>
          {fishingReports.length > 0 ? (
            <>
              <p className={styles.reportCount}>{totalCount} Fishing Reports</p>
              <ul className={styles.list}>
                <div className={styles.frRows}>
                  {fishingReports.map((fishingReport, i) => {
                    return (
                      <li key={fishingReport.id} className={styles.card}>
                        <FishingReportCard
                          fishingReport={fishingReport}
                          position={i}
                          onLakeClick={() => {
                            WebAnalytics.productDetailPageClick('[reports].(lake_link)');
                          }}
                          onMediaClick={() => {
                            WebAnalytics.productDetailPageClick('[reports].(media_link)');
                          }}
                          onProductClick={() => {
                            WebAnalytics.productDetailPageClick('[reports].(product_link)');
                          }}
                          onUserClick={() => {
                            WebAnalytics.productDetailPageClick('[reports].(user_link)');
                          }}
                          alternateHeroPixelWidth={heroImageWidth}
                        />
                      </li>
                    );
                  })}
                </div>
              </ul>

              <Pagination
                current={page}
                onChange={onPaginationChange}
                pageSize={perPage}
                total={totalCount}
                showSizeChanger={false}
                hideOnSinglePage={true}
                className={styles.pagination}
              />
            </>
          ) : (
            <div className={styles.noResults}>
              <p>
                {selectedStateAbbr
                  ? `Sorry, we don't yet have fishing reports for ${
                      allStates.filter((s) => s.abbr === selectedStateAbbr)[0].name
                    }.`
                  : "Sorry, we don't yet have fishing reports that match that criteria."}{' '}
                Want to help us get started?!
              </p>
              <OmniaButton
                kind="secondary"
                size="lg"
                fontSize={14}
                onPress={() => {
                  dispatch(FishingReportModalActions.FISHING_REPORT_MODAL_OPEN());
                }}
              >
                <FormOutlined /> Submit a Fishing Report
              </OmniaButton>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
